import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import { getFolderMetaErrorBreakdown, type ApprovalScope } from '@/services/api/template-library';

interface Props {
    folderId: number;
    hasErrors: boolean;
    scope?: ApprovalScope;
}

export const MetaErrorBreakdown = ({ folderId, hasErrors, scope = 'all' }: Props) => {
    const [open, setOpen] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['folder-meta-error-breakdown', folderId, scope],
        queryFn: () => getFolderMetaErrorBreakdown(folderId, scope),
        enabled: open && hasErrors,
        staleTime: 1000 * 30,
    });

    if (!hasErrors) return null;

    return (
        <div className="rounded-lg border bg-red-50/40 text-xs">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 text-red-700 hover:bg-red-50"
            >
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">Ver detalhes dos erros</span>
                {data && <span className="text-gray-500">({data.total_errors})</span>}
            </button>

            {open && (
                <div className="px-3 pb-3 pt-1 space-y-1">
                    {isLoading && <div className="text-gray-500 py-2">Carregando…</div>}
                    {data && data.by_category.length === 0 && (
                        <div className="text-gray-500 py-2">Nenhum erro categorizado.</div>
                    )}
                    {data?.by_category.map((cat) => (
                        <details key={cat.key} className="border rounded bg-white">
                            <summary className="cursor-pointer flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50">
                                <span className="font-mono text-red-700 w-12 text-right">{cat.qty}</span>
                                <span className="text-gray-800 flex-1">{cat.label}</span>
                                <span className="text-gray-500">
                                    {cat.pages_affected} page{cat.pages_affected !== 1 ? 's' : ''}
                                </span>
                            </summary>
                            {cat.sample && (
                                <div className="px-3 py-2 border-t bg-gray-50 text-gray-600 font-mono text-[11px] break-words">
                                    {cat.sample}
                                </div>
                            )}
                        </details>
                    ))}
                </div>
            )}
        </div>
    );
};
